import { AppTheme } from '@backstage/core-plugin-api';
import {
  BackstageTypography,
  createUnifiedTheme,
  defaultTypography,
  genPageTheme,
  palettes,
  shapes,
  UnifiedThemeOptions,
  UnifiedThemeProvider,
} from '@backstage/theme';
import LightIcon from '@material-ui/icons/WbSunny';
import DarkIcon from '@material-ui/icons/Brightness2';
import { Theme } from '@material-ui/core/styles';
import { createElement } from 'react';

// QED brand tokens. Source: qed-website src/assets/styles/main.css (@theme),
// via the qed-brand skill. Names below match the site's token names.
const navy = '#011e30';
const navy800 = '#011624';
const navy300 = '#155b7d'; // muted
const navyLight = '#0f2035';
const cyan = '#c6e1f3'; // body on dark
const cyan100 = '#8cc5e9';
const blue = '#2b71d0'; // links
const yellow900 = '#ffc336'; // the one accent
const offWhite = '#f7f7f7';
const grayBorder = '#e5e7eb';
const darkBorder = 'rgba(198, 225, 243, 0.16)'; // cyan at low alpha

const lightPalette = {
  ...palettes.light,
  primary: { main: navy },
  secondary: { main: yellow900 },
  link: blue,
  text: { primary: navy, secondary: navy300 },
  divider: grayBorder,
  background: {
    default: offWhite,
    paper: '#ffffff',
  },
  navigation: {
    background: navy,
    indicator: yellow900,
    color: cyan,
    selectedColor: '#ffffff',
    navItem: { hoverBackground: navyLight },
  },
};

const darkPalette = {
  ...palettes.dark,
  primary: { main: cyan100 },
  secondary: { main: yellow900 },
  link: cyan100,
  text: { primary: '#ffffff', secondary: cyan },
  divider: darkBorder,
  background: {
    default: navy800,
    paper: navy,
  },
  navigation: {
    background: navy800,
    indicator: yellow900,
    color: cyan,
    selectedColor: '#ffffff',
    navItem: { hoverBackground: navyLight },
  },
};

// Body: Manrope. Headings: Plus Jakarta Sans 800. Both loaded in public/index.html.
// Typography (not component overrides) so TechDocs picks the heading font up too:
// its reader injects theme.typography.h1..h6 into the docs shadow DOM.
const fontFamily = '"Manrope", system-ui, sans-serif';
const headingFontFamily = '"Plus Jakarta Sans", system-ui, sans-serif';
const heading = (level: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6') => ({
  ...defaultTypography[level],
  fontFamily: headingFontFamily,
  fontWeight: 800,
});
const typography: BackstageTypography = {
  ...defaultTypography,
  fontFamily,
  h1: heading('h1'),
  h2: heading('h2'),
  h3: heading('h3'),
  h4: heading('h4'),
  h5: heading('h5'),
  h6: heading('h6'),
};

const headerColors = [navy, navy800];

// Backstage component names (CatalogReact*, BackstageTable*) are not in MUI's
// Components type, though createUnifiedTheme accepts them at runtime.
const backstageComponents = {
  CatalogReactCatalogFilterLayout: {
    styleOverrides: {
      root: {
        flexDirection: 'column !important',
      },
      filterBox: {
        width: '100% !important',
        maxWidth: 'none !important',
        marginRight: '0 !important',
        marginBottom: '16px',
      },
    },
  },
  BackstageTableHeader: {
    styleOverrides: {
      header: ({ theme }: { theme: Theme }) => ({
        padding: theme.spacing(1.5, 1.5),
        background: theme.palette.background.default,
        color: theme.palette.text.secondary,
        fontSize: '0.6875rem',
        fontWeight: 700,
        lineHeight: 1.3,
        letterSpacing: '0.08em',
        borderTop: 'none',
        borderBottom: `1px solid ${theme.palette.divider}`,
      }),
    },
  },
} as unknown as UnifiedThemeOptions['components'];

// Tables follow the brand card grammar: hairline dividers, off-white header,
// generous cell padding, hover as the only row state. Header labels are small
// uppercase with positive tracking; body text carries no tracking.
const components: UnifiedThemeOptions['components'] = {
  MuiPaper: {
    styleOverrides: {
      outlined: { borderColor: grayBorder },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: ({ theme }) => ({
        padding: theme.spacing(1.5, 1.5),
        fontSize: '0.875rem',
        fontWeight: 500,
        lineHeight: 1.5,
        borderBottom: `1px solid ${theme.palette.divider}`,
      }),
      head: ({ theme }) => ({
        color: theme.palette.text.secondary,
        fontSize: '0.6875rem',
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
      }),
      alignRight: { fontVariantNumeric: 'tabular-nums' },
    },
  },
  MuiTableRow: {
    styleOverrides: {
      root: ({ theme }) => ({
        '&:hover': { backgroundColor: theme.palette.action.hover },
      }),
      head: { '&:hover': { backgroundColor: 'transparent' } },
    },
  },
  ...backstageComponents,
};

const pageTheme = {
  home: genPageTheme({ colors: headerColors, shape: shapes.wave }),
  documentation: genPageTheme({ colors: headerColors, shape: shapes.wave }),
  tool: genPageTheme({ colors: headerColors, shape: shapes.wave }),
  service: genPageTheme({ colors: headerColors, shape: shapes.wave }),
  website: genPageTheme({ colors: headerColors, shape: shapes.wave }),
  library: genPageTheme({ colors: headerColors, shape: shapes.wave }),
  other: genPageTheme({ colors: headerColors, shape: shapes.wave }),
  app: genPageTheme({ colors: headerColors, shape: shapes.wave }),
  apis: genPageTheme({ colors: headerColors, shape: shapes.wave }),
};

const customLightTheme = createUnifiedTheme({
  palette: lightPalette,
  defaultPageTheme: 'home',
  typography,
  pageTheme,
  components,
});

const customDarkTheme = createUnifiedTheme({
  palette: darkPalette,
  defaultPageTheme: 'home',
  typography,
  pageTheme,
  components,
});

export const lightTheme: AppTheme = {
  id: 'light',
  title: 'Light',
  variant: 'light',
  icon: createElement(LightIcon),
  Provider: ({ children }) =>
    createElement(UnifiedThemeProvider, { theme: customLightTheme, children }),
};

export const darkTheme: AppTheme = {
  id: 'dark',
  title: 'Dark',
  variant: 'dark',
  icon: createElement(DarkIcon),
  Provider: ({ children }) =>
    createElement(UnifiedThemeProvider, { theme: customDarkTheme, children }),
};
